import { useMutation } from '@tanstack/react-query';

import { type PracticeLocation, useSetDefaultPracticeLocationOptions } from '@/entities/practice';
import { useConfirmDialogStore } from '@/shared/stores/confirm-dialog-store';
import { SettingsRowActionButton } from '@/shared/ui/settings/settings-row';

type Props = {
    location: PracticeLocation;
    // True while another row's editor is open elsewhere on the page.
    disabled?: boolean;
};

// Never rendered for the location that is already the default — the caller filters that out.
export function SetDefaultLocationButton({ location, disabled }: Props) {
    const setDefaultLocation = useMutation(useSetDefaultPracticeLocationOptions());
    const openConfirmDialog = useConfirmDialogStore((state) => state.open);

    const onClick = () => {
        openConfirmDialog({
            title: 'Change default?',
            // Copy is design-authored, not phrased here.
            description: (
                <>
                    Your <strong>{location.name}</strong> location will be selected by default as
                    the location for all new sessions.
                </>
            ),
            confirmText: 'Make Default',
            cancelText: 'Cancel',
            onConfirm: () => setDefaultLocation.mutate(location.id),
        });
    };

    return (
        <SettingsRowActionButton
            aria-label={`Make Default ${location.name}`}
            onClick={onClick}
            disabled={disabled || setDefaultLocation.isPending}
        >
            Make Default
        </SettingsRowActionButton>
    );
}
